import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, formatCurrency } from '@platform/core';
import { Project } from '../../types/budget.types';
import { ProjectProgressBar } from './ProjectProgressBar';
import { useBudget } from '../../contexts/BudgetContext';

interface ProjectRowProps {
  project: Project;
  onDelete: (projectId: string) => void;
  currency?: string;
  expandedContent?: React.ReactNode;
}

// Helper to format currency safely (empty string causes Intl.NumberFormat to crash)
const formatAmount = (amount: number, currency?: string) => {
  try {
    return formatCurrency(amount, currency || undefined);
  } catch {
    return amount.toLocaleString();
  }
};

export const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  onDelete,
  currency,
  expandedContent,
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { calculateProjectProgress, updateProject } = useBudget();
  const [isExpanded, setIsExpanded] = useState(false);

  const progress = calculateProjectProgress(project);

  const handleStatusChange = async (status: string) => {
    await updateProject(project.id, { status: status as any });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return t('budget.projects.statusActive');
      case 'completed': return t('budget.projects.statusCompleted');
      case 'future': return t('budget.projects.statusFuture');
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'completed': return 'status-completed';
      case 'future': return 'status-future';
      default: return '';
    }
  };

  return (
    <>
      <tr className={`project-row ${isExpanded ? 'expanded' : ''}`}>
        <td className="expand-cell">
          <button
            className="expand-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        </td>
        <td className="name-cell">
          <strong
            className="project-name-link"
            onClick={() => navigate(`/projects/${project.id}`)}
            title={t('project.openDetails') || 'Open project details'}
          >
            {project.name}
          </strong>
          {project.description && (
            <div className="description">{project.description}</div>
          )}
        </td>
        <td>{project.client || '-'}</td>
        <td>{project.projectManager || '-'}</td>
        <td>{project.manager || '-'}</td>
        <td>{project.contractNumber || '-'}</td>
        <td>
          <select
            className={`status-select ${getStatusClass(project.status)}`}
            value={project.status}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="active">{t('budget.projects.statusActive')}</option>
            <option value="completed">{t('budget.projects.statusCompleted')}</option>
            <option value="future">{t('budget.projects.statusFuture')}</option>
          </select>
        </td>
        <td className="text-end amount">
          {project.budget ? formatAmount(project.budget, currency) : '-'}
        </td>
        <td className={`text-end ${progress.profit >= 0 ? 'text-success' : 'text-danger'}`}>
          {progress.profit !== undefined ? formatAmount(progress.profit, currency) : '-'}
        </td>
        <td className={`text-end ${progress.profitPercent >= 0 ? 'text-success' : 'text-danger'}`}>
          {progress.profitPercent !== undefined ? `${progress.profitPercent}%` : '-'}
        </td>
        <td>
          <ProjectProgressBar value={progress.timeConsumed} />
        </td>
        <td>
          <ProjectProgressBar value={progress.budgetConsumed} variant="warning" />
        </td>
        <td>
          <ProjectProgressBar value={progress.invoicesIssued} variant="success" />
        </td>
        <td>
          <button
            className="btn btn-sm btn-icon btn-danger"
            onClick={() => onDelete(project.id)}
            title={t('common.delete')}
          >
            ×
          </button>
        </td>
      </tr>
      {isExpanded && expandedContent && (
        <tr className="expanded-row">
          <td colSpan={14}>
            <div className="expanded-content">
              {expandedContent}
            </div>
          </td>
        </tr>
      )}
      <style>{`
        .project-row {
          transition: background-color 0.2s;
        }

        .project-row:hover {
          background: var(--color-background);
        }

        .project-row.expanded {
          background: var(--color-background);
        }

        .expand-cell {
          width: 40px;
          text-align: center;
        }

        .expand-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          color: var(--color-text-secondary);
          font-size: 0.75rem;
        }

        .expand-btn:hover {
          color: var(--color-primary);
        }

        .name-cell strong {
          display: block;
        }

        .project-name-link {
          cursor: pointer;
          color: var(--color-primary);
          transition: color 0.2s;
        }

        .project-name-link:hover {
          color: var(--color-primary-dark, #1d4ed8);
          text-decoration: underline;
        }

        .name-cell .description {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          margin-top: 0.25rem;
        }

        .status-select {
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.75rem;
          background: var(--color-surface);
          cursor: pointer;
        }

        .status-select.status-active {
          background: #dcfce7;
          border-color: #86efac;
          color: #166534;
        }

        .status-select.status-completed {
          background: #e0e7ff;
          border-color: #a5b4fc;
          color: #3730a3;
        }

        .status-select.status-future {
          background: #fef3c7;
          border-color: #fcd34d;
          color: #92400e;
        }

        .text-end {
          text-align: end;
        }

        .text-success {
          color: #22c55e;
        }

        .text-danger {
          color: #ef4444;
        }

        .amount {
          font-family: var(--font-mono);
        }

        .btn-icon {
          width: 24px;
          height: 24px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
        }

        .btn-danger {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .btn-danger:hover {
          background: #fecaca;
        }

        .expanded-row td {
          padding: 0 !important;
          border-bottom: 2px solid var(--color-border);
        }

        .expanded-content {
          padding: 1rem;
          background: var(--color-surface);
        }
      `}</style>
    </>
  );
};
